import React, { useState, useEffect, useContext } from 'react';
import { SettingsContext } from '../../context/SettingsProvider';
import open from '../../assets/icons/eye-regular.svg';
import closed from '../../assets/icons/eye-slash-regular.svg';
import saveIcon from '../../assets/icons/save.svg';
import SuccessNotification from '../../components/toast/SuccessNotification';
import ErrorNotification from '../../components/toast/ErrorNotification';
import ConfirmModal from '../../components/modals/ConfirmModal';
import PasswordStrengthIndicator from '../../components/display/PasswordStrengthIndicator';
import zxcvbn from 'zxcvbn';

const validatePassword = (password) => {
  const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
  return pattern.test(password);
};

export default function ProfilePage() {
  const { getUser, updateUser, user } = useContext(SettingsContext);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    passwordConfirmation: '',
    currentPassword: '',
  });
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [text, setText] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCurrentPasswordModal, setShowCurrentPasswordModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else if (field === 'passwordConfirmation') {
      setShowPasswordConfirmation(!showPasswordConfirmation);
    } else if (field === 'currentPassword') {
      setShowCurrentPassword(!showCurrentPassword);
    }
  };

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({ ...formData, username: user.username });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    const updatedFormData = {
      ...formData,
      [id]: value,
    };
    setFormData(updatedFormData);
    if (id === 'password' || id === 'passwordConfirmation') {
      if (updatedFormData.password && updatedFormData.passwordConfirmation) {
        setPasswordsMatch(
          updatedFormData.password === updatedFormData.passwordConfirmation
        );
      } else {
        setPasswordsMatch(true);
      }
    }
  };

  const startSpinner = () => {
    setShowSpinner(true);
    setTimeout(() => {
      setShowSpinner(false);
    }, 3000);
  };

  const closeNotification = () => {
    setShowSuccess(false);
    setShowError(false);
  };

  const handleCancelPasswordChange = () => {
    setIsChangingPassword(false);
    setFormData({
      ...formData,
      password: '',
      passwordConfirmation: '',
      currentPassword: '',
    });
    setPasswordsMatch(true);
    setShowPassword(false);
    setShowPasswordConfirmation(false);
    setShowCurrentPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      formData.username !== user.username &&
      (!formData.password || !formData.passwordConfirmation)
    ) {
      setShowError(true);
      setText('Password is required to change the username.');
      return;
    }

    if (
      (formData.password || formData.passwordConfirmation) &&
      !passwordsMatch
    ) {
      setShowError(true);
      setText('Passwords do not match.');
      return;
    }

    if (formData.password) {
      if (!validatePassword(formData.password)) {
        setShowError(true);
        setText(
          'Your password must be at least 8 characters long, and include at least one uppercase letter, one lowercase letter, one number, and one special character.'
        );
        return;
      }
      const passwordScore = zxcvbn(formData.password).score;
      if (passwordScore < 3) {
        setShowError(true);
        setText('Password is too weak. Please choose a stronger password.');
        return;
      }
      setShowCurrentPasswordModal(true);
      return;
    }

    let confirmMessage = '';
    if (formData.username !== user.username) {
      confirmMessage = 'Are you sure you would like to change your username?';
    }

    if (confirmMessage) {
      setConfirmText(confirmMessage);
      setShowConfirmModal(true);
    } else {
      setShowError(true);
      setText('No changes to update');
    }
  };

  const handleCurrentPasswordConfirm = async () => {
    if (!formData.currentPassword) {
      setShowError(true);
      setText('Please enter your current password');
      return;
    }

    setShowCurrentPasswordModal(false);
    startSpinner();

    try {
      await updateUser({ ...formData, verifyCurrentPassword: true }, true);
      setShowSuccess(true);
      setText('Profile updated successfully');
      setFormData({
        ...formData,
        password: '',
        passwordConfirmation: '',
        currentPassword: '',
      });
      setIsChangingPassword(false);
    } catch (error) {
      setShowError(true);
      setText('Profile update failed. Please verify your current password is correct.');
    } finally {
      setShowSpinner(false);
    }
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    startSpinner();
    try {
      await updateUser(formData, true);
      setShowSuccess(true);
      setText('Profile updated successfully');
      setIsChangingPassword(false);
    } catch (error) {
      setShowError(true);
      setText('Profile update failed');
    } finally {
      setShowSpinner(false);
    }
  };

  return (
    <div>
      <div id="profile" className="w-full px-4 py-6 sm:w-3/4 sm:px-12">
        <div className="max-w-4xl rounded-lg bg-white p-6 shadow-lg dark:bg-stone-900">
          <form onSubmit={handleSubmit}>
            {!isChangingPassword && (
              <div className="mb-5">
                <label
                  htmlFor="username"
                  className="mb-2 block select-none dark:text-gray-200"
                >
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  className="w-1/2 rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                  value={formData.username}
                  maxLength="50"
                  onChange={handleInputChange}
                  autoComplete="off"
                />
              </div>
            )}

            {isChangingPassword && (
              <>
                <div className="relative mb-5">
                  <label
                    htmlFor="password"
                    className="mb-2 block select-none dark:text-gray-200"
                  >
                    New Password
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    className="w-1/2 rounded-md border border-gray-300 px-3 py-2 pr-10 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                    value={formData.password}
                    maxLength="256"
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    name="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('password')}
                    className="absolute inset-y-0 right-[435px] top-8 flex cursor-pointer items-center"
                  >
                    <img
                      src={showPassword ? open : closed}
                      alt="Toggle"
                      className="h-6 w-6 text-gray-500"
                    />
                  </button>
                </div>
                <div className="relative mb-5">
                  <label
                    htmlFor="passwordConfirmation"
                    className="mb-2 block select-none dark:text-gray-200"
                  >
                    Confirm New Password
                    {!passwordsMatch && (
                      <small className="text-red-500">
                        {' '}
                        (Passwords do not match){' '}
                      </small>
                    )}
                  </label>
                  <input
                    type={showPasswordConfirmation ? 'text' : 'password'}
                    id="passwordConfirmation"
                    className="w-1/2 rounded-md border border-gray-300 px-3 py-2 pr-10 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                    value={formData.passwordConfirmation}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    name="new-password-confirmation"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('passwordConfirmation')}
                    className="absolute inset-y-0 right-[435px] top-8 flex cursor-pointer items-center"
                  >
                    <img
                      src={showPasswordConfirmation ? open : closed}
                      alt="Toggle"
                      className="h-6 w-6 text-gray-500"
                    />
                  </button>
                </div>
                <div className="w-1/2 mb-5">
                  <PasswordStrengthIndicator password={formData.password} />
                </div>
              </>
            )}

            <div className="mt-5 flex items-center gap-3">
              <button
                type="submit"
                className="rounded-md bg-blue-600 px-6 py-2 text-white hover:bg-blue-600 focus:bg-blue-600 dark:bg-indigo-500 dark:hover:bg-indigo-500"
              >
                {showSpinner ? (
                  <div className="mr-2.5 inline-block h-3 w-3 animate-spin rounded-full border-b-2 border-gray-300" />
                ) : (
                  <img
                    src={saveIcon}
                    alt="Save"
                    className="bottom-2 mr-1.5 inline-block h-4 w-4"
                  />
                )}
                {showSpinner ? 'Saving' : 'Save Changes'}
              </button>
              {!isChangingPassword && (
                <button
                  type="button"
                  onClick={() => setIsChangingPassword(true)}
                  className="rounded-md border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-semibold px-4 py-1.5 flex items-center"
                >
                  Change Password
                </button>
              )}
              {isChangingPassword && (
                <button
                  type="button"
                  onClick={handleCancelPasswordChange}
                  className="w-full sm:w-32 py-1.5 rounded-md bg-gray-500 hover:bg-gray-600 text-white font-semibold border-2 border-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
        {showSuccess && (
          <SuccessNotification
            show={showSuccess}
            onClose={closeNotification}
            text={text}
          />
        )}
        {showError && (
          <ErrorNotification
            show={showError}
            onClose={closeNotification}
            text={text}
          />
        )}
        {showConfirmModal && (
          <ConfirmModal
            title="Confirmation"
            onCancel={() => setShowConfirmModal(false)}
            onConfirm={handleConfirm}
          >
            <p>{confirmText}</p>
          </ConfirmModal>
        )}
        {showCurrentPasswordModal && (
          <div className="fixed inset-0 bg-stone-950 bg-opacity-75 overflow-y-auto h-full w-full flex items-center justify-center">
            <div className="relative mx-auto p-5 w-96 shadow-lg rounded-md bg-white dark:bg-stone-900">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-200">
                  Verify Current Password
                </h3>
                <div className="mt-2 relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    id="currentPassword"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 pr-10 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                    value={formData.currentPassword}
                    onChange={handleInputChange}
                    placeholder="Enter current password"
                    autoComplete="new-password"
                    name="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('currentPassword')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
                  >
                    <img
                      src={showCurrentPassword ? open : closed}
                      alt="Toggle"
                      className="h-5 w-5 text-gray-500"
                    />
                  </button>
                </div>
                <div className="mt-4 flex justify-end space-x-2">
                  <button
                    onClick={() => setShowCurrentPasswordModal(false)}
                    className="neutral"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCurrentPasswordConfirm}
                    className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 dark:bg-indigo-600 dark:hover:bg-indigo-600"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}